/**
 * <s-product-gallery> — One large product image with a thumbnail strip below it.
 *
 * The stage is a horizontal scroll-snap track (swipeable on touch); thumbnails jump to a slide and
 * the thumbnail for the slide in view is marked aria-current="true".
 *
 * Required markup:
 *   <s-product-gallery id="…">
 *     [data-gallery-stage]                      — overflow-x scroll-snap track
 *       [data-gallery-slide]                    — one per media, in order
 *     button[commandfor="(same id)"][command="--show"][data-index] — thumbnails
 *   </s-product-gallery>
 *
 * Optional: data-variant-media='{"<variant id>": <slide index>}' moves the stage to a variant's photo
 * when a variant input (any [data-variant-id] or <option data-variant-id>) changes on the page, and
 * data-initial-index shows that slide on load (deep links with ?variant=). A thumbnail with
 * data-variant-id also selects that variant in the picker.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const stage = this.querySelector('[data-gallery-stage]');
    const slides = [...this.querySelectorAll('[data-gallery-slide]')];
    if (!stage || slides.length < 2) return;

    this.addEventListener('command', (event) => {
      if (event.command !== '--show') return;
      const index = Number.parseInt(event.source?.dataset.index || '0', 10);
      this.#show(stage, slides, index, true);
      if (event.source?.dataset.variantId) this.#selectVariant(event.source.dataset.variantId);
    }, { signal: this.#ac.signal });

    let variantMedia = {};
    try {
      variantMedia = JSON.parse(this.dataset.variantMedia || '{}');
    } catch {
      variantMedia = {};
    }
    // Variant pickers live outside the gallery and are re-rendered on change, so listen on the document.
    document.addEventListener('change', (event) => {
      const input = event.target;
      const variantId = input?.dataset?.variantId || input?.selectedOptions?.[0]?.dataset?.variantId;
      if (!variantId || !(variantId in variantMedia)) return;
      this.#show(stage, slides, variantMedia[variantId], true);
    }, { signal: this.#ac.signal });

    const initialIndex = Number.parseInt(this.dataset.initialIndex || '0', 10);
    if (initialIndex > 0) this.#show(stage, slides, initialIndex, false);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.#mark(slides.indexOf(entry.target));
        }
      }
    }, { root: stage, threshold: [0.6] });
    slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #show(stage, slides, index, animate) {
    const slide = slides[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the track so the page itself does not jump.
    stage.scrollTo({ left: slide.offsetLeft - stage.offsetLeft, behavior: animate && !reduce ? 'smooth' : 'auto' });
    this.#mark(index);
  }

  // Picks the variant in the page's variant picker, which then updates price, SKU and the form.
  // If no picker input matches (e.g. the photo's variant needs other options changed too), the photo just shows.
  #selectVariant(variantId) {
    const radio = document.querySelector(`input[type="radio"][data-variant-id="${CSS.escape(variantId)}"]`);
    if (radio) {
      if (!radio.checked && !radio.disabled) radio.click();
      return;
    }
    const option = document.querySelector(`select option[data-variant-id="${CSS.escape(variantId)}"]`);
    const select = option?.closest('select');
    if (select && !option.selected && !option.disabled) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #mark(index) {
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const current = Number.parseInt(thumb.dataset.index || '-1', 10) === index;
      thumb.setAttribute('aria-current', current ? 'true' : 'false');
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
